import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@elkaso-app/web-design';
import type { FamilyMatrixVariant } from 'apis/exclusive-deals';
import { useGetFamilyPriceMatrixApi } from 'apis/exclusive-deals';
import { useGetMasterProductFamilyApi } from 'apis/master-product-families';
import { groupAttributesByType } from 'apis/master-products';
import { BaseSortButton } from 'components/base-table/sorting';
import { usePageParams } from 'hooks/use-page-params';
import { AddSupplierControl } from 'pages/prices/components/add-supplier-control';
import { AddVariantControl } from 'pages/prices/components/add-variant-control';
import { AttributeChips } from 'pages/prices/components/attribute-chips';
import { MatrixViewControls } from 'pages/prices/components/matrix-view-controls';
import { PriceCell } from 'pages/prices/components/price-cell';
import type { MatrixSupplier } from 'pages/prices/utils/build-price-matrix';
import { buildPriceMatrix } from 'pages/prices/utils/build-price-matrix';
import { applyMatrixViewFilter, sortDirectionFromParam, sortRowsByBrandSpec } from 'pages/prices/utils/matrix-rows';
import { uomFilterValues, uomLadder } from 'pages/prices/utils/uom-display';
import { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiFilter, FiX } from 'react-icons/fi';

export interface PriceMatrixProps {
  familyId: number;
}

const SCALAR_TYPES = ['brand', 'origin', 'state', 'spec'] as const;

// The matrix opens showing only priced rows updated within the last week.
const DEFAULT_RECENT_DAYS = '7';

// All attribute values a variant carries, per dimension: the scalar fields,
// the UOM ladder rungs (pack/item/size) and the EAV attributes (a type can
// appear in both — merged, deduped).
function variantAttributeValues(variant: FamilyMatrixVariant): Record<string, string[]> {
  const values: Record<string, string[]> = {};

  const add = (type: string, value: string | null | undefined) => {
    if (!value || !value.trim()) return;
    const list = values[type] ?? (values[type] = []);
    if (!list.includes(value)) list.push(value);
  };

  SCALAR_TYPES.forEach((type) => add(type, variant[type]));
  Object.entries(uomFilterValues(variant)).forEach(([type, value]) => add(type, value));
  Object.entries(groupAttributesByType(variant.attributes)).forEach(([type, list]) =>
    list.forEach((value) => add(type, value))
  );

  return values;
}

// True when the variant's values satisfy every active selection — ANY of a
// dimension's selected values matches (OR within, AND across dimensions) —
// optionally ignoring one dimension (used to decide chip availability).
function matchesSelections(
  values: Record<string, string[]>,
  selections: Record<string, string[]>,
  excludeType?: string
): boolean {
  return Object.entries(selections).every(([type, selected]) => {
    if (type === excludeType || !selected.length) return true;
    const carried = values[type];
    return carried ? selected.some((value) => carried.includes(value)) : false;
  });
}

// Row label per the design handoff: "Brand Spec" title, "Origin · State"
// subtitle (plus any single-valued extras the title doesn't carry).
function rowTitle(variant: FamilyMatrixVariant): string {
  return [variant.brand, variant.spec].filter(Boolean).join(' ') || variant.name || `#${variant.id}`;
}

function rowSubtitle(variant: FamilyMatrixVariant): string {
  return [variant.origin, variant.state].filter(Boolean).join(' · ');
}

function rowUnit(variant: FamilyMatrixVariant): string {
  return `AED/${variant.packUnit || variant.itemUnit || variant.baseUnit}`;
}

export const PriceMatrix = ({ familyId }: PriceMatrixProps) => {
  const { data: matrixData, isLoading } = useGetFamilyPriceMatrixApi(familyId);
  // Row order lives in the URL (`?sort=brand.asc|desc`, written by the header
  // sort button) so it survives family switches and reloads like every other
  // table's sort. Default is A→Z.
  const { getPageParams } = usePageParams();
  const sortDirection = sortDirectionFromParam(getPageParams().sort);

  const variants = useMemo(() => matrixData?.data?.variants ?? [], [matrixData]);
  const deals = useMemo(() => matrixData?.data?.deals ?? [], [matrixData]);

  const { suppliers, rows } = useMemo(() => {
    const matrix = buildPriceMatrix(variants, deals);
    // Alphabetical by brand then spec (Sadia 0.9, Sadia 1.0, Seara 0.9…).
    return { suppliers: matrix.suppliers, rows: sortRowsByBrandSpec(matrix.rows, sortDirection) };
  }, [variants, deals, sortDirection]);

  // A family with no variants yet (just created here, typically) can't take
  // its name from a variant — fall back to the family record.
  const { data: family } = useGetMasterProductFamilyApi(variants.length ? undefined : familyId);

  // Supplier columns added locally that have no deal yet — they materialize in
  // `suppliers` once their first price lands, so drop those to avoid doubles.
  const [addedSuppliers, setAddedSuppliers] = useState<MatrixSupplier[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  // Variants created inline here: shown past the view filters (they have no
  // price yet) so the operator can type one straight away.
  const [pinnedVariantIds, setPinnedVariantIds] = useState<Set<number>>(() => new Set());
  const [isAddVariantOpen, setIsAddVariantOpen] = useState(false);
  useEffect(() => {
    setAddedSuppliers([]);
    setIsAddOpen(false);
    setPinnedVariantIds(new Set());
    setIsAddVariantOpen(false);
  }, [familyId]);

  const rowValues = useMemo(() => rows.map((row) => variantAttributeValues(row.variant)), [rows]);

  // EAV attribute types present on any variant. Unlike the scalar dimensions
  // (every variant has some brand/origin/state/spec, so one distinct value
  // means "all share it"), an attribute can be carried by only SOME variants —
  // single-valued attribute dimensions still discriminate and must show.
  const attributeTypes = useMemo(() => {
    const types = new Set<string>();
    variants.forEach((variant) => variant.attributes.forEach((attribute) => types.add(attribute.attributeType)));
    return types;
  }, [variants]);

  // dimension -> selected values (multi-select: OR within a dimension, AND
  // across); reset when switching families, along with the row-level filters.
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  // Facet chips collapse by default to give the matrix the vertical space;
  // the operator's open/closed choice sticks across family switches.
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  // Hide rows with no priced cell at all — on by default.
  const [isPricedOnly, setIsPricedOnly] = useState(true);
  // Keep only rows with a price updated in the last N days ('' = off);
  // defaults to a week so the matrix opens on fresh prices.
  const [recentDaysInput, setRecentDaysInput] = useState(DEFAULT_RECENT_DAYS);
  useEffect(() => {
    setSelections({});
    setIsPricedOnly(true);
    setRecentDaysInput(DEFAULT_RECENT_DAYS);
  }, [familyId]);

  // dimension -> all values across the family's variants, first-seen order.
  // Scalar dimensions where every variant shares one value are hidden (handoff
  // rule: a facet with nothing to narrow is noise — the title/subtitle already
  // carry those). Attribute dimensions always show when present.
  const chipGroups = useMemo(() => {
    const groups: Record<string, string[]> = {};
    rowValues.forEach((values) => {
      Object.entries(values).forEach(([type, list]) => {
        const all = groups[type] ?? (groups[type] = []);
        list.forEach((value) => {
          if (!all.includes(value)) all.push(value);
        });
      });
    });
    return Object.fromEntries(
      Object.entries(groups).filter(([type, list]) => list.length > 1 || attributeTypes.has(type))
    );
  }, [rowValues, attributeTypes]);

  const toggleValue = (type: string, value: string) => {
    setSelections((prev) => {
      const current = prev[type] ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [type]: next };
    });
  };

  const recentDays = Number(recentDaysInput);
  const isRecentFilterOn = recentDaysInput.trim() !== '' && Number.isFinite(recentDays) && recentDays >= 0;

  // Facets narrow the variant rows first; the view filters then prune the
  // CELL grid — stale/unpriced cells, then rows and supplier columns left
  // with nothing to show.
  const facetRows = rows.filter((_, index) => matchesSelections(rowValues[index], selections));
  const { rows: visibleRows, suppliers: visibleSuppliers } = applyMatrixViewFilter(facetRows, suppliers, {
    pricedOnly: isPricedOnly,
    recentDays: isRecentFilterOn ? recentDays : null,
    pinnedVariantIds,
  });

  // Locally added supplier columns always stay — the operator opened them to
  // type prices into, so a view filter must not take them away.
  const visibleSupplierIds = new Set(visibleSuppliers.map((supplier) => supplier.id));
  const columns = [...visibleSuppliers, ...addedSuppliers.filter((supplier) => !visibleSupplierIds.has(supplier.id))];

  // A chip is disabled when no row under the OTHER active selections carries it.
  const disabledValues = (type: string) =>
    (chipGroups[type] ?? []).filter(
      (value) =>
        !rowValues.some((values) => values[type]?.includes(value) && matchesSelections(values, selections, type))
    );

  const familyName = variants[0]?.name ?? family?.universalName ?? '';

  const addVariantControl = (
    <AddVariantControl
      familyId={familyId}
      familyName={familyName}
      isOpen={isAddVariantOpen}
      onOpen={() => setIsAddVariantOpen(true)}
      onCancel={() => setIsAddVariantOpen(false)}
      onCreated={(product) => {
        setPinnedVariantIds((prev) => new Set(prev).add(product.id));
        setIsAddVariantOpen(false);
      }}
    />
  );

  if (isLoading) {
    return (
      <Flex justify='center' align='center' h='full'>
        <Spinner size='md' color='red.500' />
      </Flex>
    );
  }

  // A family with zero variants has nothing to render rows from — the only
  // case that still gets a full empty state. It offers the inline variant
  // form so a family created here can be populated on the spot.
  if (!variants.length) {
    return (
      <Flex direction='column' align='center' justify={isAddVariantOpen ? 'flex-start' : 'center'} h='full' p='lg'>
        {familyName && (
          <Heading as='h1' fontSize={{ base: '19px', md: '24px' }} fontWeight='semibold' alignSelf='flex-start' mb='lg'>
            {familyName}
          </Heading>
        )}
        <Box textAlign='center' maxW={isAddVariantOpen ? '960px' : '380px'} w='full'>
          <Flex
            justify='center'
            align='center'
            w='60px'
            h='60px'
            mx='auto'
            mb='18px'
            borderRadius='16px'
            bg='#F7F7F8'
            border='1px solid'
            borderColor='gray.200'
            color='#C9CCD3'
            fontSize='26px'>
            ⌗
          </Flex>
          <Heading as='h3' fontSize='19px' fontWeight='semibold' mb='6px'>
            No variants in this family
          </Heading>
          <Text fontSize='13.5px' color='gray.500' lineHeight='1.5' mb='18px'>
            Add a variant to start pricing it.
          </Text>
          <Box textAlign='start'>{addVariantControl}</Box>
        </Box>
      </Flex>
    );
  }

  // The "no prices yet" callout speaks about the family's true state, so it
  // ignores the view filters — a filter that prunes every column must not
  // masquerade as an empty family.
  const hasColumns = suppliers.length > 0 || addedSuppliers.length > 0;

  const hasFacets = Object.keys(chipGroups).length > 0;
  // Active facet selections, flattened for the collapsed summary and counted
  // for the toggle badge (a toggled-off dimension leaves an empty array).
  const activeSelections = Object.entries(selections).filter(([, values]) => values.length > 0);
  const activeCount = activeSelections.reduce((count, [, values]) => count + values.length, 0);

  return (
    <Flex direction='column' h='full' minW={0}>
      <Stack
        spacing='10px'
        p={{ base: '12px 14px 10px', md: '14px 26px 12px' }}
        borderBottom='1px solid'
        borderColor='gray.200'>
        <Flex align='center' gap='12px' wrap='wrap' rowGap='6px'>
          <Heading as='h1' fontSize={{ base: '19px', md: '24px' }} fontWeight='semibold' letterSpacing='-0.01em'>
            {familyName}
          </Heading>
          {hasFacets && (
            <Button
              ms='auto'
              flexShrink={0}
              size='sm'
              h='32px'
              px='14px'
              borderRadius='full'
              fontSize='13px'
              fontWeight='semibold'
              variant={activeCount ? 'solid' : 'outline'}
              colorScheme={activeCount ? 'red' : 'gray'}
              borderColor='gray.300'
              color={activeCount ? undefined : 'gray.700'}
              boxShadow='sm'
              _hover={activeCount ? undefined : { bg: 'gray.100' }}
              leftIcon={<FiFilter size={14} />}
              rightIcon={isFiltersOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              onClick={() => setIsFiltersOpen((prev) => !prev)}>
              Filters{activeCount ? ` · ${activeCount}` : ''}
            </Button>
          )}
        </Flex>
        {hasFacets && isFiltersOpen && (
          // One inline group per dimension, flowing onto shared lines — short
          // dimensions (origin, state…) no longer cost a full row each.
          <Flex wrap='wrap' align='center' sx={{ columnGap: '20px', rowGap: '7px' }}>
            {Object.entries(chipGroups).map(([type, values]) => (
              <AttributeChips
                key={type}
                label={type}
                values={values}
                selectedValues={selections[type] ?? []}
                disabledValues={disabledValues(type)}
                onToggle={(value) => toggleValue(type, value)}
              />
            ))}
            {activeCount > 0 && (
              <Button
                size='xs'
                h='22px'
                px='8px'
                fontSize='11px'
                fontWeight='medium'
                variant='ghost'
                colorScheme='gray'
                onClick={() => setSelections({})}>
                Clear all
              </Button>
            )}
          </Flex>
        )}
        {hasFacets && !isFiltersOpen && activeCount > 0 && (
          // Collapsed facets still narrow the table — keep the active values
          // in sight as removable chips so the filtering is never invisible.
          <Flex wrap='wrap' align='center' sx={{ columnGap: '6px', rowGap: '5px' }}>
            {activeSelections.map(([type, values]) =>
              values.map((value) => (
                <Button
                  key={`${type}:${value}`}
                  size='xs'
                  h='22px'
                  px='9px'
                  fontSize='11px'
                  fontWeight='semibold'
                  borderRadius='full'
                  variant='solid'
                  colorScheme='red'
                  rightIcon={<FiX size={11} />}
                  onClick={() => toggleValue(type, value)}>
                  {value}
                </Button>
              ))
            )}
            <Button
              size='xs'
              h='22px'
              px='8px'
              fontSize='11px'
              fontWeight='medium'
              variant='ghost'
              colorScheme='gray'
              onClick={() => setSelections({})}>
              Clear all
            </Button>
          </Flex>
        )}
      </Stack>

      <MatrixViewControls
        visibleCount={visibleRows.length}
        totalCount={rows.length}
        recentDaysInput={recentDaysInput}
        isRecentFilterOn={isRecentFilterOn}
        onRecentDaysInputChange={setRecentDaysInput}
        isPricedOnly={isPricedOnly}
        onPricedOnlyChange={setIsPricedOnly}
      />

      {!hasColumns && (
        <Flex
          align='center'
          gap='12px'
          wrap='wrap'
          rowGap='10px'
          m={{ base: '14px 12px', md: '20px 18px' }}
          p='14px 16px'
          border='1px dashed'
          borderColor='gray.300'
          borderRadius='9px'
          bg='#FAFAFB'>
          <Text fontSize='18px' color='gray.400'>
            ⌗
          </Text>
          <Box>
            <Text fontSize='13.5px' fontWeight='semibold'>
              No supplier prices yet for {familyName || 'this family'}
            </Text>
            <Text fontSize='12.5px' color='gray.500'>
              Add a supplier column to start recording quotes — supplier prices mapped to these variants fill this
              matrix automatically.
            </Text>
          </Box>
          <Button
            ms='auto'
            flexShrink={0}
            size='sm'
            colorScheme='red'
            fontSize='13px'
            borderRadius='9px'
            onClick={() => setIsAddOpen(true)}>
            + Add a supplier
          </Button>
        </Flex>
      )}

      <Box flex='1' overflow='auto'>
        <Table size='sm' sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead position='sticky' top={0} bg='#F7F7F8' zIndex={2}>
            <Tr>
              {/* w='1%' + nowrap content = shrink-to-fit: the SKU column takes
                  its content width instead of absorbing the table's free
                  space and shoving the price columns to the right edge. */}
              <Th
                position='sticky'
                left={0}
                bg='#F7F7F8'
                zIndex={1}
                w='1%'
                minW={{ base: '150px', md: '220px' }}
                p='11px 18px'
                borderRight='1px solid'
                borderRightColor='gray.200'>
                <Flex align='center' gap='4px'>
                  <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.08em' color='gray.500'>
                    SKU · brand · spec
                  </Text>
                  {/* Sort control lives in the column header, like every other
                      table: cycles A→Z (default) → Z→A → default. */}
                  <BaseSortButton sortBy='brand' />
                </Flex>
              </Th>
              {/* Columns are sized to the price, not the supplier name — the
                  name wraps onto multiple lines so more cells fit on screen. */}
              {columns.map((supplier) => (
                <Th key={supplier.id} w='110px' minW='110px' p='9px 10px' textAlign='end'>
                  <Text
                    fontSize='10.5px'
                    fontWeight='bold'
                    letterSpacing='0.07em'
                    color='gray.600'
                    whiteSpace='normal'
                    wordBreak='break-word'
                    lineHeight='1.3'
                    noOfLines={3}>
                    {supplier.name ?? `#${supplier.id}`}
                  </Text>
                  <Text fontSize='10px' fontWeight='medium' color='gray.400' textTransform='none' mt='1px'>
                    supplier
                  </Text>
                </Th>
              ))}
              {/* Flexible filler: absorbs the table's leftover width so the
                  price columns stay beside the SKU column instead of being
                  distributed toward the right edge. Collapses to nothing when
                  the columns overflow into a horizontal scroll. */}
              <Th w='100%' p={0} />
            </Tr>
          </Thead>
          <Tbody>
            {visibleRows.map((row) => (
              <Tr key={row.variant.id}>
                <Td
                  position='sticky'
                  left={0}
                  bg='white'
                  zIndex={1}
                  p='9px 18px'
                  borderBottom='1px solid'
                  borderBottomColor='#ECEDF0'
                  borderRight='1px solid'
                  borderRightColor='gray.200'>
                  <Box minW={0}>
                    <Flex align='center' gap='8px'>
                      <Text fontSize='13.5px' fontWeight='semibold' whiteSpace={{ base: 'normal', md: 'nowrap' }}>
                        {rowTitle(row.variant)}
                      </Text>
                      <Text
                        as='span'
                        flexShrink={0}
                        fontSize='10.5px'
                        fontWeight='semibold'
                        color='red.600'
                        bg='red.50'
                        border='1px solid'
                        borderColor='red.100'
                        px='8px'
                        py='2px'
                        borderRadius='6px'
                        whiteSpace={{ base: 'normal', md: 'nowrap' }}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {rowUnit(row.variant)}
                      </Text>
                    </Flex>
                    <Text fontSize='11.5px' color='gray.500' whiteSpace={{ base: 'normal', md: 'nowrap' }}>
                      {rowSubtitle(row.variant)}
                    </Text>
                    <Text
                      fontSize='11px'
                      color='gray.500'
                      whiteSpace={{ base: 'normal', md: 'nowrap' }}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {uomLadder(row.variant)}
                    </Text>
                  </Box>
                </Td>
                {columns.map((supplier) => {
                  const cell = row.cells[supplier.id];
                  return (
                    <Td
                      key={supplier.id}
                      p={0}
                      verticalAlign='middle'
                      borderBottom='1px solid'
                      borderBottomColor='#ECEDF0'
                      borderRight='1px solid'
                      borderRightColor='#ECEDF0'
                      bg={cell?.isLowest ? '#EFF8F2' : undefined}>
                      <PriceCell
                        cell={cell}
                        supplierId={supplier.id}
                        masterProductId={row.variant.id}
                        variant={row.variant}
                      />
                    </Td>
                  );
                })}
                <Td p={0} borderBottom='1px solid' borderBottomColor='#ECEDF0' />
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Flex
          align='flex-start'
          gap='10px'
          wrap='wrap'
          direction={isAddVariantOpen ? 'column' : 'row'}
          p={{ base: '12px 12px 32px', md: '16px 18px 40px' }}>
          <AddSupplierControl
            excludeIds={columns.map((supplier) => supplier.id)}
            isOpen={isAddOpen}
            onOpen={() => setIsAddOpen(true)}
            onCancel={() => setIsAddOpen(false)}
            onAdd={(supplier) => {
              setAddedSuppliers((prev) => (prev.some((s) => s.id === supplier.id) ? prev : [...prev, supplier]));
              setIsAddOpen(false);
            }}
          />
          {addVariantControl}
        </Flex>
      </Box>
    </Flex>
  );
};
